import { useState, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";



const TYPES = [
  { key: "resume", icon: "fa-file-arrow-up",  color: "var(--fc-primary)", bg: "var(--fc-primary-light)", title: "Resume Upload",  desc: "Upload a resume file" },
  { key: "manual", icon: "fa-pen-to-square",   color: "#16A34A", bg: "var(--fc-green-light)", title: "Manual Entry",   desc: "Fill in candidate details" },
  { key: "bulk",   icon: "fa-table",           color: "#8B5CF6", bg: "var(--fc-purple-light)", title: "Bulk CSV Upload", desc: "Upload multiple candidates" },
];

const emptyForm = { firstName:"", lastName:"", role:"", email:"", contactEmail:"", phone:"", officePhone:"", bio:"" };

function Field({ label, required, children, error }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" }}>
        {label}{required && " *"}
      </label>
      {children}
      {error && <div style={{ fontSize:11, color:"var(--fc-red)", marginTop:3 }}>{error}</div>}
    </div>
  );
}

const inputStyle = { width:"100%", boxSizing:"border-box", padding:"9px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)" };

export default function AddCandidateModal({ onClose, onAdd }) {
  const { userSlug } = useAuth();

  const [step,      setStep]      = useState("type");
  const [type,      setType]      = useState(null);
  const [file,      setFile]      = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [progress,  setProgress]  = useState(0);
  const fileRef = useRef();
  const csvRef  = useRef();

  const set = (k, v) => { setForm(f => ({...f,[k]:v})); setErrors(e => ({...e,[k]:""})); };

  const selectType = (t) => {
    setType(t);
    setError("");
    if (t === "resume") setStep("upload");
    if (t === "manual") { setForm(emptyForm); setStep("form"); }
    if (t === "bulk")   setStep("bulk");
  };

  const reset = () => { setType(null); setStep("type"); setForm(emptyForm); setFile(null); setError(""); setErrors({}); setProgress(0); };

  // ── Resume upload → create-resource-from-resume ──────────────────────────
  const handleResumeUpload = async () => {
    if (!file) { setError("Please select a file"); return; }
    setLoading(true); setError(""); setProgress(0);
    const interval = setInterval(() => setProgress(p => p < 85 ? p + 10 : p), 300);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("resume_upload", true);
    fd.append("file", file);
    try {
      const res = await axios.post(`${API_BASE}create-resource-from-resume`, fd);
      clearInterval(interval); setProgress(100);
      if (res.data?.status === 1) {
        setTimeout(() => { if (onAdd) onAdd(res.data); onClose(); }, 400);
      } else {
        setError(res.data?.message || "Upload failed. Please try again.");
        setLoading(false); setProgress(0);
      }
    } catch {
      clearInterval(interval);
      setError("Network error. Please try again.");
      setLoading(false); setProgress(0);
    }
  };

  // ── Manual entry → create-resource ───────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim())  e.lastName  = "Required";
    if (!form.email.trim())     e.email     = "Required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleManualSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true); setError("");
    const fd = new FormData();
    fd.append("user_slug",        userSlug);
    fd.append("first_name",       form.firstName);
    fd.append("last_name",        form.lastName);
    fd.append("user_profile_role",form.role);
    fd.append("profile_bio",      form.bio);
    fd.append("email",            form.email);
    fd.append("contact_email",    form.contactEmail || form.email);
    fd.append("phone",            form.phone);
    fd.append("officePhone",      form.officePhone);
    try {
      const res = await axios.post(`${API_BASE}create-resource`, fd);
      if (res.data?.status === 1) {
        if (onAdd) onAdd(res.data);
        onClose();
      } else {
        setError(res.data?.message || "Failed to create candidate.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  // ── Bulk CSV → upload-resource-csv ───────────────────────────────────────
  const handleBulkUpload = async () => {
    if (!file) { setError("Please select a CSV file"); return; }
    setLoading(true); setError(""); setProgress(0);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("file", file);
    try {
      const res = await axios.post(`${API_BASE}upload-resource-csv`, fd, {
        onUploadProgress: e => setProgress(Math.round(e.loaded * 100 / e.total)),
      });
      if (res.data?.status === 1) {
        if (onAdd) onAdd(res.data);
        onClose();
      } else {
        setError(res.data?.message || "Upload failed.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false); setProgress(0);
  };

  const steps = { type:"Choose Method", upload:"Upload Resume", form:"Enter Details", bulk:"Bulk Upload" };

  const title = (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ width:34, height:34, borderRadius:9, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className="fa-solid fa-user-plus" style={{ color:"var(--fc-primary)", fontSize:14 }} />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Add Candidate</div>
        <div style={{ fontSize:11, color:"var(--fc-text-muted)", marginTop:1 }}>{steps[step]}</div>
      </div>
    </div>
  );

  // Left side of footer: Back once past the first step, Cancel on the first step
  const leftButton = step !== "type" ? (
    <button onClick={reset} disabled={loading}
      style={{ padding:"8px 16px", borderRadius:9, border:"1.5px solid var(--fc-border)", background:"var(--fc-card-bg)", fontSize:12, fontWeight:600, color:"var(--fc-text-muted)", cursor:"pointer", fontFamily:"inherit" }}>
      <i className="fa-solid fa-arrow-left" style={{fontSize:10,marginRight:6}} />Back
    </button>
  ) : (
    <button onClick={onClose}
      style={{ padding:"8px 16px", borderRadius:9, border:"1.5px solid var(--fc-border)", background:"var(--fc-card-bg)", fontSize:12, fontWeight:600, color:"var(--fc-text-muted)", cursor:"pointer", fontFamily:"inherit" }}>
      Cancel
    </button>
  );

  // Right side: step-specific primary action - none at all on the "type" step
  const confirmProps = step === "upload"
    ? { onConfirm: handleResumeUpload, confirmLabel: <>{!loading && <i className="fa-solid fa-upload" style={{fontSize:11}} />} {loading ? "Uploading..." : "Upload & Create"}</>, confirmDisabled: !file, confirmBusy: loading }
    : step === "form"
    ? { onConfirm: handleManualSubmit, confirmLabel: <>{!loading && <i className="fa-solid fa-user-plus" style={{fontSize:11}} />} {loading ? "Creating..." : "Add Candidate"}</>, confirmBusy: loading }
    : step === "bulk"
    ? { onConfirm: handleBulkUpload, confirmLabel: <>{!loading && <i className="fa-solid fa-table" style={{fontSize:11}} />} {loading ? "Uploading..." : "Upload CSV"}</>, confirmDisabled: !file, confirmBusy: loading }
    : {};

  return (
    <Modal title={title} onClose={!loading ? onClose : undefined} maxWidth={520}
      footer={<ModalFooter left={leftButton} {...confirmProps} />}>

      {/* Error */}
      {error && (
        <div style={{ padding:"10px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"var(--fc-red)" }}>
          <i className="fa-solid fa-circle-exclamation" style={{marginRight:7}} />{error}
        </div>
      )}

      {/* TYPE SELECTION */}
      {step === "type" && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)", marginBottom:6 }}>How would you like to add a candidate?</p>
          {TYPES.map(t => (
            <button key={t.key} onClick={() => selectType(t.key)}
              style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 16px", borderRadius:12, border:"1.5px solid var(--fc-border)", background:"var(--fc-card-bg)", cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=t.color;e.currentTarget.style.background=t.bg;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--fc-border)";e.currentTarget.style.background="var(--fc-card-bg)";}}>
              <div style={{ width:40, height:40, borderRadius:10, background:t.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className={`fa-solid ${t.icon}`} style={{ color:t.color, fontSize:16 }} />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)" }}>{t.title}</div>
                <div style={{ fontSize:12, color:"var(--fc-text-muted)" }}>{t.desc}</div>
              </div>
              <i className="fa-solid fa-chevron-right" style={{ color:"var(--fc-text-hint)", fontSize:11 }} />
            </button>
          ))}
        </div>
      )}

      {/* RESUME UPLOAD */}
      {step === "upload" && (
        <div>
          <div
            onClick={() => !loading && fileRef.current?.click()}
            style={{ border:"2px dashed var(--fc-border)", borderRadius:12, padding:"32px 20px", textAlign:"center", cursor:loading?"wait":"pointer", transition:"all 0.15s", marginBottom:16,
              ...(file ? { borderColor:"var(--fc-primary)", background:"var(--fc-primary-light)" } : {}) }}
            onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor="var(--fc-primary)";}}
            onDragLeave={e=>{e.currentTarget.style.borderColor=file?"var(--fc-primary)":"var(--fc-border)";}}
            onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){setFile(f);setError("");}}}
          >
            <i className="fa-solid fa-file-arrow-up" style={{ fontSize:28, color:file?"var(--fc-primary)":"var(--fc-text-hint)", display:"block", marginBottom:10 }} />
            {file ? (
              <><div style={{ fontSize:14, fontWeight:700, color:"var(--fc-primary)" }}>{file.name}</div><div style={{ fontSize:12, color:"var(--fc-text-muted)", marginTop:4 }}>Click to change</div></>
            ) : (
              <><div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-secondary)" }}>Drop resume here or click to browse</div><div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:4 }}>PDF, DOC, DOCX supported</div></>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" style={{ display:"none" }}
            onChange={e=>{const f=e.target.files[0];if(f){setFile(f);setError("");}}} />

          {loading && (
            <div style={{ marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--fc-text-muted)", marginBottom:5 }}>
                <span>Uploading & parsing...</span><span>{progress}%</span>
              </div>
              <div style={{ height:6, background:"var(--fc-border-light)", borderRadius:99, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${progress}%`, background:"var(--fc-primary)", borderRadius:99, transition:"width 0.3s" }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* MANUAL FORM */}
      {step === "form" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
            <Field label="First Name" required error={errors.firstName}>
              <input value={form.firstName} onChange={e=>set("firstName",e.target.value)} placeholder="First name"
                style={{...inputStyle, borderColor:errors.firstName?"var(--fc-red)":"var(--fc-border)"}} />
            </Field>
            <Field label="Last Name" required error={errors.lastName}>
              <input value={form.lastName} onChange={e=>set("lastName",e.target.value)} placeholder="Last name"
                style={{...inputStyle, borderColor:errors.lastName?"var(--fc-red)":"var(--fc-border)"}} />
            </Field>
          </div>
          <Field label="Job Role / Title">
            <input value={form.role} onChange={e=>set("role",e.target.value)} placeholder="e.g. Senior Developer" style={inputStyle} />
          </Field>
          <Field label="Login Email" required error={errors.email}>
            <input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="candidate@email.com"
              style={{...inputStyle, borderColor:errors.email?"var(--fc-red)":"var(--fc-border)"}} />
          </Field>
          <div style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"10px 12px", background:"var(--fc-primary-light)", borderRadius:9, marginBottom:14, fontSize:12, color:"var(--fc-text-secondary)" }}>
            <i className="fa-solid fa-circle-info" style={{ color:"var(--fc-primary)", marginTop:2, flexShrink:0 }} />
            <span>A temporary password will be generated automatically and emailed to this address.</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
            <Field label="Phone">
              <input value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="+1 000-000-0000" style={inputStyle} />
            </Field>
            <Field label="Office Phone">
              <input value={form.officePhone} onChange={e=>set("officePhone",e.target.value)} placeholder="Office number" style={inputStyle} />
            </Field>
          </div>
          <Field label="Contact Email">
            <input type="email" value={form.contactEmail} onChange={e=>set("contactEmail",e.target.value)} placeholder="Same as login if blank" style={inputStyle} />
          </Field>
          <Field label="Bio / Summary">
            <textarea value={form.bio} onChange={e=>set("bio",e.target.value)} placeholder="Brief professional summary..." rows={3}
              style={{...inputStyle, resize:"vertical"}} />
          </Field>
        </div>
      )}

      {/* BULK CSV */}
      {step === "bulk" && (
        <div>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)", marginBottom:14 }}>Upload a CSV with columns: <code>first_name, last_name, email, role, phone</code></p>
          <div
            onClick={() => !loading && csvRef.current?.click()}
            style={{ border:"2px dashed var(--fc-border)", borderRadius:12, padding:"28px 20px", textAlign:"center", cursor:loading?"wait":"pointer",
              ...(file ? { borderColor:"#8B5CF6", background:"var(--fc-purple-light)" } : {}) }}>
            <i className="fa-solid fa-table" style={{ fontSize:26, color:file?"#8B5CF6":"var(--fc-text-hint)", display:"block", marginBottom:8 }} />
            {file ? (
              <><div style={{ fontSize:14, fontWeight:700, color:"#8B5CF6" }}>{file.name}</div><div style={{ fontSize:12, color:"var(--fc-text-muted)", marginTop:3 }}>Click to change</div></>
            ) : (
              <><div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-secondary)" }}>Drop CSV file here</div><div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:3 }}>CSV files only</div></>
            )}
          </div>
          <input ref={csvRef} type="file" accept=".csv" style={{ display:"none" }}
            onChange={e=>{const f=e.target.files[0];if(f){setFile(f);setError("");}}} />

          {loading && (
            <div style={{ marginTop:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--fc-text-muted)", marginBottom:5 }}>
                <span>Uploading...</span><span>{progress}%</span>
              </div>
              <div style={{ height:6, background:"var(--fc-border-light)", borderRadius:99, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${progress}%`, background:"#8B5CF6", borderRadius:99, transition:"width 0.3s" }} />
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
